/*
 * Copyright © 2017-Present, The Freshwater Trust, all rights reserved.
 */

import React, {useEffect, useMemo, useRef, useState} from "react";
import {useDebouncedEffect} from "../../../Hooks/EffectHooks";
import {useSafeState} from "../../../Hooks/useSafeState";
import {StreamBankAutoCompleteItem} from "../../../Components/Common/StreamBankAutoCompleteItem";
import CreatableSelect from "react-select/creatable";

const customStyles = {
	control: (provided) => ({
		...provided,
		borderRadius: '5px', // Set the border-radius of the control
		height: '36px',       // Set the height of the control (which includes the input)
	}),
	// input: (provided) => ({
	// 	...provided,
	// 	height: '40px',       // Set the height of the input
	// 	margin: '0px',        // Remove any margin (optional)
	// }),
};

const _ServiceDrivenAutoCompleteFieldEditor = (props, Service) => {
	const {
		CustomData = null,
		emptyMessage = "No matching results",
		searchingMessage = "Loading...",
		filter = CustomData?.filter ?? ((values) => values),
		FieldDef:fieldDef,
		Value:initialValue = null,
		placeholder = null,
		children = null,
		autoFocus,

		OnUpdateModel = () => {},
		OnUpdate,

		isLoading,
		isValidNewOption = () => false,
		onInputChange,
		getNewOptionData,
		onCreateOption = undefined,
		formatCreateLabel,
	} = props;
	
	const additionalSearchParams = {
		...CustomData?.additionalSearchParams,
		...props?.additionalSearchParams,
	}
	
	const {
		context = null,
		allowNull = fieldDef?.allowNull ?? false,
		// Pickers restricted to one level of a hierarchy can opt out of the subtext,
		// where naming that level only restates what the field already means.
		showSubtitle = true,
	} = props;
	
	const [searching, setSearching] = useState(false);
	const searchInFlightRef = useRef(false);
	const paramsAtLastSearch = useRef(null);
	const [value, setValue] = useState(""); // the search term (note this is different from `props.Value` which is a PK)
	const [options, setOptions] = useSafeState([]); // the search results (to show in the dropdown menu)
	const [selectedModel, setSelectedModel] = useState(null); // the model result that corresponds to the selected value
	
	const searchTerms = (value ?? "").toLowerCase().split(" ");

	const searchParameters = useMemo(
		() => ({value, context, ...additionalSearchParams}),
		[value, context, JSON.stringify(additionalSearchParams)]
	);
	
	const loadModel = async (value) => {
		try {
			return (await Service.FindById(value))?.result ?? null;
		} catch (e) {
			console.log('loadModel failed for value:', value, e);
		}
		return null;
	};
	
	useEffect(() => {
		if(initialValue == null || initialValue === '0') {
			// new models assign 'undefined' to the parent's key
			// which might make us re-fetch and wipe out the new model data
			// '0' is used in parent keys that are required
			return;
		}
		const loadInitialModel = async () => {
			const result = await loadModel(initialValue);
			selectModel(result, true);
		};
		void loadInitialModel();
	}, [initialValue]);

	const selectModel = (item, skipUpdate = false) => {
		setSelectedModel(item);
		if (!skipUpdate) {
			OnUpdate?.(Service.GetPrimaryKey(item));
			// TODO: maybe we should only run one of these or the other
			// because they can clobber each other, but I'm putting this
			// one to be called last since it by-default won't do anything
			// and if it is overridden it likely means we intend it to be
			// the overriding logic.
			OnUpdateModel?.(item);
		}
	};
	
	useDebouncedEffect(
		async () => {
			if(searching) {
				if (searchInFlightRef.current) return;
				searchInFlightRef.current = true;
				paramsAtLastSearch.current = JSON.stringify(searchParameters);
				try {
					await performSearch();
				} finally {
					searchInFlightRef.current = false;
					setSearching(false);
				}
			} else if (paramsAtLastSearch.current !== null &&
					   paramsAtLastSearch.current !== JSON.stringify(searchParameters)) {
				// Params changed while a search was in flight — re-fire with latest
				setSearching(true);
			}
		},
		[searching, searchParameters],
		250
	);

	const performSearch = async () => {

		// don't perform a new search if our
		// criteria matches the selectedModel
		if (selectedModel && Service.GetTitle(selectedModel) === value) {
			setOptions([selectedModel]);
			return;
		}

		const searchPromise = context ? Service.SearchWithContext(value, context, additionalSearchParams)
			: Service.Search(value, additionalSearchParams);

		try {
			const results = (await searchPromise)?.result;
			setOptions(results ?? []);
		} catch (x) {
			console.log(x);
		}

		return searchPromise;
	};
	
	const defaultFormatCreateLabel = (inputValue) => {
		let title = `new ${Service.ModelName}`;
		if (inputValue) {
			title = `"${inputValue}" as ` + title;
		}
		return `+ Add ${title}`;
	}
	
	const subtitle = showSubtitle ? Service.GetSubtitle(selectedModel) : null;
	const filteredValues = filter([...options], selectedModel);
	
	return <div className="autocomplete-column">
		<CreatableSelect
			value={selectedModel}
			onChange={(selectedOption, actionMeta) => {
				const skipUpdate = (actionMeta.action !== 'create-option')
					&& Service.GetPrimaryKey(selectedOption) === Service.GetPrimaryKey(selectedModel);
				selectModel(selectedOption, skipUpdate);
			}}
			
			inputValue={value}
			onInputChange={(inputValue, actionMeta) => {
				setValue(inputValue);
				setSearching(true);
				onInputChange?.(inputValue, actionMeta);
			}}

			onFocus={() => {
				if (!options.length) {
					setSearching(true);
				}
			}}
			
			isClearable={allowNull}
			// isLoading={searching}
			isSearchable={true}
			
			options={searching ? [] : filteredValues}
			formatOptionLabel={(option, formatOptionLabelMeta) => {
				if(formatOptionLabelMeta.context === 'value') {
					return Service.GetTitle(option);
				}
				return <StreamBankAutoCompleteItem item={option} service={Service} title={option.label} />
			}}
			
			getOptionLabel={(option) => Service.GetTitle(option)}
			getOptionValue={(option) => Service.GetPrimaryKey(option)}
			
			placeholder={placeholder ?? Service.ModelName}
			
			noOptionsMessage={() => (searching ? searchingMessage : emptyMessage)}
			// onBlur={onBlur}
			// menuIsOpen={menuIsOpen}
			// onMenuOpen={() => void setMenuIsOpen(false) }
		    // onMenuClose={() => void setMenuIsOpen(false)}
			// isDisabled={readOnly}
			
			autoFocus={autoFocus}
			menuPosition="fixed"
			styles={customStyles}
			
			filterOption={(option) => {
				if(!searchTerms) { return false; }
				const searchableText = Service.GetSearchableString(option?.data);
				return searchTerms.some(searchTerm => searchableText.includes(searchTerm));
			}}
			
			// create option props
			isLoading={isLoading}
			isValidNewOption={isValidNewOption}
			formatCreateLabel={formatCreateLabel ?? defaultFormatCreateLabel}
			getNewOptionData={getNewOptionData}
			onCreateOption={onCreateOption}
		/>
		{subtitle != null ? <div className="sb-autocomplete-subtext">{subtitle}</div> : null}
		{children}
	</div>;
};

export function ServiceDrivenAutoCompleteFieldEditor(Service) {
	const component = (props) => _ServiceDrivenAutoCompleteFieldEditor(props, Service);
	component.displayName = `${Service.ModelName}FieldEditor`;
	return component;
}
